import * as focusTrap from 'focus-trap';
import { tabbable } from 'tabbable';
import { attachSheetSwipe } from './sheetGesture.js';

// Shared by every <elm-modal> so focus-trap keeps only the topmost trap active
// when two modals are open, instead of two traps fighting over focus.
const trapStack = [];

const openModals = new Set();

const OPEN_ESCAPE_LAYER = '[data-escape-layer="open"]';

const LIVE_OUTSIDE_MODAL = '.ck-body-wrapper, .toast-container, draggable-window[data-window-layer="above-clinical"]';

const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || event.defaultPrevented) return;

  const topmost = [...openModals].at(-1);
  if (!topmost) return;

  event.preventDefault();
  const layer = [...topmost.querySelectorAll(OPEN_ESCAPE_LAYER)].at(-1);
  if (layer) {
    layer.dispatchEvent(new CustomEvent('close'));
  } else if (topmost.dataset.dismissible === 'true') {
    topmost.dispatchEvent(new CustomEvent('close'));
  }
});

// Tab belongs to the dialog too. focus-trap only steps in at the first and
// last tabbable, so anywhere between, the browser picks the next stop. Safari
// skips buttons and links unless "Press Tab to highlight each item" is on, so
// it finds nothing in the dialog and leaves for the address bar. Walking the
// same list focus-trap uses keeps every browser inside. Safari only rings focus
// it moved itself, so every move asks for the ring, which is why focus-trap
// does not wrap on its own.
const TABBABLE = { getShadowRoot: true };

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Tab' || event.defaultPrevented) return;

  const topmost = [...openModals].at(-1);
  if (!topmost?.trap?.active || !topmost.contains(document.activeElement)) return;

  event.preventDefault();

  const stops = tabbable(topmost, TABBABLE);
  if (stops.length === 0) return;

  nextStop(stops, focusedElement(), event.shiftKey).focus({ focusVisible: true });
});

// Inside a shadow root, document.activeElement is only the host.
function focusedElement() {
  let element = document.activeElement;
  while (element?.shadowRoot?.activeElement) element = element.shadowRoot.activeElement;
  return element;
}

// By position in the list, which is Tab order: positive tabindex first, then
// the document, shadow roots included. A radio group is one stop, as browsers
// make it: tabbable lists every option of a group with nothing checked, so
// stepping skips the rest of the group the focus is already in.
function nextStop(stops, focused, backward) {
  const index = stops.indexOf(focused);
  if (index === -1) return firstStopFrom(stops, focused, backward);

  const step = backward ? -1 : 1;
  for (let offset = 1; offset < stops.length; offset++) {
    const stop = stops[(((index + step * offset) % stops.length) + stops.length) % stops.length];
    if (!inSameRadioGroup(stop, focused)) return stop;
  }
  return focused;
}

function inSameRadioGroup(node, other) {
  return (
    node.type === 'radio' &&
    other.type === 'radio' &&
    node.name !== '' &&
    node.name === other.name &&
    node.form === other.form
  );
}

// Focus on something that is not a stop, such as the dialog itself: the next
// stop is the next one after it in the document.
function firstStopFrom(stops, focused, backward) {
  return backward
    ? (stops.findLast((stop) => follows(focused, stop)) ?? stops.at(-1))
    : (stops.find((stop) => follows(stop, focused)) ?? stops[0]);
}

function follows(node, reference) {
  return Boolean(reference.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING);
}

function isCKEditorPopup(node) {
  return node.closest('.ck') != null;
}

// A click on another <elm-modal> is "outside" this trap, so without allowing it
// each active trap would preventDefault the other modal's buttons — deadlocking
// the portal when two modals are open at once.
function isInAnyModal(node) {
  return node.closest('elm-modal') != null;
}

function isLiveOutsideModal(node) {
  return node.closest(LIVE_OUTSIDE_MODAL) != null;
}

function composedParent(node) {
  return node.assignedSlot ?? (node.parentNode instanceof ShadowRoot ? node.parentNode.host : node.parentNode);
}

function inertUnlessLive(element, inerted) {
  if (element.inert || element.matches(LIVE_OUTSIDE_MODAL)) return;

  if (element.querySelector(LIVE_OUTSIDE_MODAL)) {
    for (const child of element.children) inertUnlessLive(child, inerted);
    return;
  }

  element.inert = true;
  inerted.push(element);
}

function makeOutsideInert(modal) {
  const inerted = [];
  for (let node = modal; node !== document.body; node = composedParent(node)) {
    for (const sibling of node.parentNode.children) {
      if (sibling !== node) inertUnlessLive(sibling, inerted);
    }
  }
  return inerted;
}

let inertedForTopmost = [];

function isolateTopmost() {
  for (const element of inertedForTopmost) element.inert = false;
  const topmost = [...openModals].filter((modal) => modal.isConnected).at(-1);
  inertedForTopmost = topmost ? makeOutsideInert(topmost) : [];
}

const MODAL_TITLE = '[data-modal-title]';

let headingIds = 0;

function freshHeadingId() {
  let id;
  do {
    id = `elm-modal-heading-${++headingIds}`;
  } while (document.getElementById(id));
  return id;
}

function nameByHeading(modal) {
  const current = modal.getAttribute('aria-labelledby');
  if (modal.hasAttribute('aria-label')) return;
  if (current && current !== modal.generatedLabel) return;

  const heading = [...modal.querySelectorAll(MODAL_TITLE)].find((title) => title.closest('elm-modal') === modal);
  if (!heading) {
    if (current) modal.removeAttribute('aria-labelledby');
    modal.generatedLabel = undefined;
    return;
  }

  heading.id ||= freshHeadingId();
  modal.generatedLabel = heading.id;
  modal.setAttribute('aria-labelledby', heading.id);
}

class ElmModal extends HTMLElement {
  connectedCallback() {
    this.previousActiveElement = document.activeElement;
    openModals.add(this);
    this.setAttribute('aria-modal', 'true');
    nameByHeading(this);
    this.headingObserver = new MutationObserver(() => nameByHeading(this));
    this.headingObserver.observe(this, { childList: true, subtree: true });
    isolateTopmost();

    this.trap = focusTrap.createFocusTrap(this, {
      allowOutsideClick: (e) => isCKEditorPopup(e.target) || isInAnyModal(e.target) || isLiveOutsideModal(e.target),
      escapeDeactivates: false,
      isKeyForward: () => false,
      isKeyBackward: () => false,
      tabbableOptions: TABBABLE,
      returnFocusOnDeactivate: false,
      trapStack,
    });

    this.trap.activate();

    this.handleGlobalFocus = this.handleGlobalFocus.bind(this);
    document.body.addEventListener('focusin', this.handleGlobalFocus, true);

    this.setupResponsiveSwipe();
  }

  disconnectedCallback() {
    this.trap.deactivate();
    this.trap = undefined;
    this.headingObserver.disconnect();
    openModals.delete(this);
    isolateTopmost();

    this.teardownResponsiveSwipe();

    if (this.previousActiveElement) {
      let focusTarget = this.previousActiveElement;

      // If focus was on a dropdown item, focus the trigger instead
      if (this.previousActiveElement.closest('.dropdown__item')) {
        const dropdown = this.previousActiveElement.closest('.dropdown');
        const trigger = dropdown?.querySelector('.dropdown__trigger');
        if (trigger) {
          focusTarget = trigger;
        }
      }

      focusTarget.focus();
    }

    this.previousActiveElement = undefined;
    document.body.removeEventListener('focusin', this.handleGlobalFocus, true);
  }

  handleGlobalFocus(e) {
    if (isCKEditorPopup(e.target)) {
      if (this.trap?.active) {
        this.trap.deactivate();
      }
    } else if (this.trap && !this.trap.active) {
      this.trap.activate();
    }
  }

  setupResponsiveSwipe() {
    if (this.dataset.dismissible !== 'true') return;
    if (this.dataset.centered === 'true') return;

    this.mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.applySwipe = () => {
      if (this.mediaQuery.matches) {
        this.attachSwipe();
      } else {
        this.detachSwipe();
      }
    };
    this.mediaQuery.addEventListener('change', this.applySwipe);
    this.applySwipe();
  }

  attachSwipe() {
    if (this.detach) return;

    const panel = this.querySelector('.modal__dialog');
    const backdrop = this.querySelector('.modal__backdrop');
    const zones = this.querySelectorAll('.modal__handle, .modal__header');
    if (!panel || !backdrop || zones.length === 0) return;

    this.detach = attachSheetSwipe({
      panel,
      backdrop,
      zones: Array.from(zones),
    });
  }

  detachSwipe() {
    if (this.detach) {
      this.detach();
      this.detach = undefined;
    }
  }

  teardownResponsiveSwipe() {
    if (this.mediaQuery && this.applySwipe) {
      this.mediaQuery.removeEventListener('change', this.applySwipe);
    }
    this.mediaQuery = undefined;
    this.applySwipe = undefined;
    this.detachSwipe();
  }
}

if (!customElements.get('elm-modal')) {
  customElements.define('elm-modal', ElmModal);
}
